import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import "@/ticketing.css";
import { Building2, Volume2, VolumeX } from "lucide-react";
import { useTranslation } from "react-i18next";
import DKFooter from "@/components/DKFooter";
import { useBranding } from "@/hooks/useBranding";
import LanguageThemeSelector from "@/components/LanguageThemeSelector";

function interpolate(tpl, vars) {
  if (!tpl) return "";
  return tpl.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? `{${k}}`);
}

function useDisplay() {
  const [data, setData] = useState(null);
  const [prevCurrent, setPrevCurrent] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/public/tickets/display");
      setData(data);
    } catch (err) {
      // Keep last snapshot on transient failures; log for observability
      console.warn("[EcranAffichage] display fetch failed", err?.message || err);
    }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 3000);
    return () => clearInterval(i);
  }, []);

  const currentNumero = data?.current?.[0]?.numero || "";
  useEffect(() => {
    if (currentNumero && currentNumero !== prevCurrent) {
      setPrevCurrent(currentNumero);
    }
  }, [currentNumero, prevCurrent]);

  return { data, currentNumero, prevCurrent };
}

function Clock() {
  const { i18n } = useTranslation();
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const i = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(i);
  }, []);
  const locale = i18n.language === "en" ? "en-GB" : "fr-FR";
  return (
    <div className="text-right">
      <div className="font-display text-3xl md:text-5xl font-bold text-white tracking-tight tabular-nums">
        {now.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
      </div>
      <div className="text-blue-200/80 text-sm uppercase tracking-[0.25em]">
        {now.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" })}
      </div>
    </div>
  );
}

function CurrentCall({ ticket, sound, isNew }) {
  useEffect(() => {
    if (!sound || !isNew || !ticket) return;
    // simple beep via WebAudio
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const gain = ctx.createGain();
      gain.gain.value = 0.15;
      gain.connect(ctx.destination);
      [880, 1100, 880].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        osc.frequency.value = freq;
        osc.connect(gain);
        const t = ctx.currentTime + i * 0.22;
        osc.start(t);
        osc.stop(t + 0.18);
      });
    } catch (err) {
      // Audio playback is non-critical (user may have muted). Log for debug only.
      console.debug("[EcranAffichage] beep failed", err?.message || err);
    }
  }, [ticket?.numero, sound, isNew]);

  if (!ticket) {
    return (
      <div className="text-center py-14">
        <div className="text-blue-200/70 uppercase tracking-[0.32em] text-sm mb-4">Prochain appel</div>
        <div className="ticket-number-huge text-white/30">— — —</div>
        <div className="text-blue-200/70 mt-6 text-2xl">Aucun ticket appelé pour le moment</div>
      </div>
    );
  }

  return (
    <div className="text-center py-6">
      <div className="text-blue-200/90 uppercase tracking-[0.32em] text-sm md:text-base mb-3">Ticket appelé</div>
      <div className="relative inline-block">
        <div className="ticket-number-huge text-transparent bg-clip-text bg-gradient-to-r from-white via-blue-200 to-violet-200" data-testid="display-current-numero">
          {ticket.numero}
        </div>
        <div className="pulse-ring pointer-events-none" />
      </div>
      <div className="mt-6 flex items-center justify-center gap-6 flex-wrap">
        <div className="text-2xl md:text-4xl text-blue-100">
          Guichet <b className="text-white font-display">{ticket.guichet || "—"}</b>
        </div>
        <div className="hidden md:block h-8 w-px bg-white/20" />
        <div className="text-2xl md:text-4xl text-blue-100">
          Service <b className="text-white font-display">{ticket.service}</b>
        </div>
      </div>
    </div>
  );
}

export default function EcranAffichage() {
  const { data, currentNumero, prevCurrent } = useDisplay();
  const { branding } = useBranding();
  const [sound, setSound] = useState(true);
  const isNew = currentNumero && currentNumero !== prevCurrent;

  const current = data?.current?.[0];
  const others = (data?.current || []).slice(1, 4);
  const upcoming = data?.upcoming || [];

  const queueColor = branding?.queue_color || "#1E3A8A";
  const secondary = branding?.secondary_color || "#0B1B4B";

  const bmsg = branding?.borne_messages || {};
  const company = branding?.company_name || "dkconnect";
  const marquee = interpolate(
    bmsg.marquee ||
      "Bienvenue chez {company} — merci de patienter dans la salle d'attente · Votre numéro s'affiche ici quand ce sera votre tour ·",
    { company }
  );
  const displayHeader = bmsg.display_header || "Écran d'affichage — salle d'attente";

  return (
    <div
      className="min-h-screen display-screen relative overflow-hidden flex flex-col"
      style={{
        background: `linear-gradient(135deg, ${queueColor} 0%, ${secondary} 55%, #2E1065 100%)`,
      }}
    >
      <div className="grain absolute inset-0 opacity-30" />

      {/* Logo watermark en fond — visible et personnalisable */}
      {branding?.logo_data_url && (
        <div className="absolute inset-0 pointer-events-none grid place-items-center opacity-[0.06] z-0" aria-hidden>
          <img
            src={branding.logo_data_url}
            alt=""
            className="w-[60vw] max-w-[720px] object-contain"
          />
        </div>
      )}

      <LanguageThemeSelector variant="floating" />

      <header className="relative z-10 px-6 md:px-10 py-4 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-white/10 backdrop-blur-md grid place-items-center border border-white/15 overflow-hidden shrink-0" data-testid="display-logo">
            {branding?.logo_data_url ? (
              <img src={branding.logo_data_url} alt="Logo entreprise" className="w-full h-full object-contain p-1" />
            ) : (
              <Building2 className="w-7 h-7 text-white" />
            )}
          </div>
          <div>
            <div className="font-display font-bold text-2xl md:text-3xl text-white tracking-tight">{branding?.company_name || "dkconnect"}</div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-blue-200">{displayHeader}</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={() => setSound((s) => !s)} className="text-blue-200 hover:text-white" title="Son" data-testid="display-sound-toggle">
            {sound ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>
          <Clock />
        </div>
      </header>

      <main className="relative z-10 grid grid-cols-1 lg:grid-cols-3 gap-6 px-6 md:px-10 py-6" data-testid="display-main">
        <div className="lg:col-span-2 bg-white/[0.05] border border-white/10 rounded-3xl backdrop-blur-xl min-h-[60vh] grid place-items-center px-6">
          <div className="w-full">
            <CurrentCall ticket={current} sound={sound} isNew={isNew} />

            {others.length > 0 && (
              <div className="mt-8 grid grid-cols-3 gap-3 max-w-3xl mx-auto">
                {others.map((t) => (
                  <div key={t.id} className="bg-white/[0.05] border border-white/10 rounded-2xl p-4 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-blue-200 mb-1">Guichet {t.guichet || "—"}</div>
                    <div className="font-display text-3xl font-bold text-white">{t.numero}</div>
                    <div className="text-xs text-blue-200/80 mt-1">{t.service}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <aside className="bg-white/[0.05] border border-white/10 rounded-3xl backdrop-blur-xl p-6" data-testid="display-upcoming">
          <div className="text-[11px] uppercase tracking-[0.32em] text-blue-200 mb-4">Prochains tickets</div>
          {upcoming.length === 0 ? (
            <div className="text-blue-200/60 py-8 text-center">File d&apos;attente vide</div>
          ) : (
            <ul className="space-y-3">
              {upcoming.slice(0, 6).map((t, idx) => (
                <li key={t.id} className="flex items-center justify-between bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-500/20 border border-blue-400/40 grid place-items-center text-blue-200 font-mono text-xs">
                      {idx + 1}
                    </div>
                    <div>
                      <div className="font-display text-xl text-white font-semibold">{t.numero}</div>
                      <div className="text-[11px] text-blue-200/70">{t.service}</div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-6 grid grid-cols-2 gap-3">
            <div className="bg-emerald-500/10 border border-emerald-400/20 rounded-xl p-3 text-center">
              <div className="text-[10px] uppercase tracking-widest text-emerald-200">Servis aujourd&apos;hui</div>
              <div className="font-display text-2xl font-bold text-white" data-testid="display-servis">{data?.stats?.servis ?? 0}</div>
            </div>
            <div className="bg-orange-500/10 border border-orange-400/20 rounded-xl p-3 text-center">
              <div className="text-[10px] uppercase tracking-widest text-orange-200">En attente</div>
              <div className="font-display text-2xl font-bold text-white" data-testid="display-en-attente">{data?.stats?.en_attente ?? 0}</div>
            </div>
          </div>
        </aside>
      </main>

      <footer className="relative z-10 border-t border-white/10 px-6 md:px-10 py-3 overflow-hidden">
        <div className="whitespace-nowrap text-blue-100/80 text-sm font-mono">
          <span className="marquee inline-block" data-testid="display-marquee">
            {marquee}
          </span>
        </div>
      </footer>
      <DKFooter variant="dark" />
    </div>
  );
}
